import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import moment from 'moment'
import { dateFilterFn } from "src/utils/era-package/filters";

export const useColumns = () => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('entryNumber', {
          header: () => 'Entry Number',
          cell: (info) => (
            <strong>
              {info.getValue()}
            </strong>
          ),
          filterFn: 'includesString',
          meta: {
            priority: 1,
            style: { whiteSpace: 'nowrap' }
          }
        }),
        columnHelper.accessor('referenceNo', {
          header: () => 'My Ref No',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('bl', {
          header: () => 'B/L Number',
          filterFn: 'includesString',
          meta: { priority: 2 },
        }),
        columnHelper.accessor((row) => row['3461Status'], {
          id: '3461Status',
          header: () => '3461',
          filterFn: 'equals',
          meta: {
            priority: 2,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor((row) => row['7501Status'], {
          id: '7501Status',
          header: () => '7501',
          filterFn: 'equals',
          meta: {
            priority: 2,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor('pga', {
          header: () => 'PGA',
          filterFn: 'equals',
          meta: {
            priority: 4,
            hideOnMobile: isMobile,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor((row) => row['1c'], {
          id: '1c',
          header: () => '1C',
          filterFn: 'equals',
          meta: {
            priority: 4,
            hideOnMobile: isMobile,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor((row) => row['1usg'], {
          id: '1usg',
          header: () => '1USG',
          filterFn: 'equals',
          meta: {
            priority: 4,
            hideOnMobile: isMobile,
            filterVariant: 'select',
          },
        }),
        columnHelper.accessor('entryPort', {
          header: () => 'Port',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('eta', {
          header: () => 'ETA',
          cell: (info) => {
            const date = info.getValue();
            if (!date) return '-';
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <FontAwesomeIcon icon={faCalendar} style={{ width: 16, height: 16 }} />
                <Typography variant="body2" whiteSpace='nowrap'>{moment(new Date(date)).format("LL")}</Typography>
              </Stack>
            );
          },
          filterFn: dateFilterFn,
          meta: { priority: 2 },
        }),
        columnHelper.accessor('statementDate', {
          header: () => 'Statement Date',
          cell: (info) => {
            const date = info.getValue();
            if (!date) return '-';
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <FontAwesomeIcon icon={faCalendar} style={{ width: 16, height: 16 }} />
                <Typography variant="body2" whiteSpace='nowrap'>{moment(new Date(date)).format("LL")}</Typography>
              </Stack>
            );
          },
          filterFn: dateFilterFn,
          meta: { priority: 4, hideOnMobile: isMobile, hideOnTablet: isTablet },
        }),
        columnHelper.accessor('importer', {
          header: () => 'Importer',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('consignee', {
          header: () => 'Consignee',
          filterFn: 'includesString',
          meta: { priority: 4, hideOnMobile: isMobile, hideOnTablet: isTablet },
        }),
        columnHelper.accessor('user', {
          header: () => 'User',
          filterFn: 'includesString',
          meta: { priority: 3, hideOnMobile: isMobile },
        }),
        columnHelper.accessor('lastModified', {
          header: () => 'Last Modified',
          cell: (info) => {
            const date = info.getValue();
            if (!date) return '-';
            return new Date(date).toLocaleString()
          },
          enableColumnFilter: false,
          meta: { priority: 5, hideOnMobile: isMobile, hideOnTablet: isTablet },
        }),
      ].filter((column) => {
        if (isSmall && column.meta?.hideOnMobile) return false;
        if (isTablet && column.meta?.hideOnTablet) return false;
        return true;
      }),
    [columnHelper, isMobile, isTablet, isSmall],
  );

  return columns
}